/**
 * Book format → image aspect ratio. Pure + unit-tested so the "correct format
 * uses correct data" guarantee (image generation + export dimensions) is checked
 * for every format the wizard can pick (portrait_2x3 / square / print_portrait /
 * print_landscape) plus the legacy a4_* formats.
 *
 * NEW books default to `portrait_2x3` (owner decision 2026-10-31): a vertical
 * 2:3 page fits A5 print at ~285 dpi from Grok 2k (1664×2496), and every image
 * provider we route to orders 2:3 NATIVELY — no fallback, no crop:
 *   - xAI / fal Grok: `aspect_ratio: "2:3"` is in their accepted set (providers/image/xai.py, fal_grok.py);
 *   - OpenAI gpt-image: 2:3 → 1024×1536, an exact 2:3 size (providers/image/openai.py);
 *   - Azure gpt-image: 2:3 → 1024×1536 (providers/image/azure_openai.py);
 *   - Gemini image (AI Studio): `aspectRatio: "2:3"` is a supported value, passed through as is.
 * Old books keep whatever format they were made in.
 */
export const DEFAULT_BOOK_FORMAT = 'portrait_2x3' as const;

export function formatAspectRatio(format: string): string {
  switch (format) {
    case 'portrait_2x3':
      return '2:3'; // Vertical book page — the default for new books (A5 / A4 portrait print)
    case 'square':
      return '1:1';
    case 'a4_landscape':
      return '16:9'; // Wide cinematic format for landscape books
    case 'a4_portrait':
      return '9:16'; // Tall format for portrait books
    case 'print_portrait':
      return '3:4'; // Print-optimized 8.5" x 11"
    case 'print_landscape':
      return '4:3'; // Print-optimized 11" x 8.5"
    default:
      return '1:1';
  }
}

/** The picture's shape as a number (width / height), for the box that holds its place on a screen. */
export function formatRatio(format?: string | null): number {
  const [w, h] = formatAspectRatio(format ?? DEFAULT_BOOK_FORMAT).split(':').map(Number);
  return w / h;
}

